import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import {
  useItemsChequeo,
  useCrearItemChequeo,
  useActualizarItemChequeo,
  useEliminarItemChequeo
} from '../../hooks/useReferencias';
import { CrudTable, CrudForm, estadoColumn } from '../../components/admin/crud';
import {
  codigoField,
  descripcionField,
  estadoField,
  categoriaField,
  esObligatorioField
} from '../../components/admin/crud/CrudForm';

import type { ItemChequeo } from '../../types';
import type { CrearItemChequeo } from '../../services/referenciasService';

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const ItemsChequeoView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // ✅ PERMISOS UAU - Control granular de botones
  const canCreate = hasPermission('items_chequeo.crear');
  const canEdit = hasPermission('items_chequeo.editar');
  const canDelete = hasPermission('items_chequeo.eliminar');

  // Estado local
  const [filtros, setFiltros] = useState({ page: 1, limit: 10 });
  const [formModal, setFormModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    data?: ItemChequeo;
  }>({
    isOpen: false,
    mode: 'create',
  });

  // Hooks de datos
  const { data, isLoading, error } = useItemsChequeo(filtros);
  const crearMutation = useCrearItemChequeo();
  const actualizarMutation = useActualizarItemChequeo();
  const eliminarMutation = useEliminarItemChequeo();

  // ===============================
  // CONFIGURACIÓN DE COLUMNAS
  // ===============================

  const columns = useMemo(() => [
    {
      key: 'codigo',
      title: t('common.code'),
      width: '120px',
    },
    {
      key: 'descripcion',
      title: t('common.description'),
      width: '200px',
    },
    {
      key: 'categoria',
      title: t('common.category'),
      width: '130px',
      render: (categoria: string) => categoria || '-',
    },
    {
      key: 'orden',
      title: t('common.order'),
      width: '80px',
      render: (orden: number) => orden || '-',
    },
    {
      key: 'es_obligatorio',
      title: t('common.mandatory'),
      width: '100px',
      render: (obligatorio: boolean) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          obligatorio
            ? 'bg-red-100 text-red-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {obligatorio ? t('common.yes') : t('common.no')}
        </span>
      ),
    },
    estadoColumn<ItemChequeo>(),
  ], [t]);

  // ===============================
  // CONFIGURACIÓN DE FILTROS
  // ===============================

  const filters = useMemo(() => [
    {
      key: 'search',
      label: t('admin.search_placeholder'),
      type: 'text' as const,
      placeholder: t('admin.search_placeholder'),
    },
    {
      key: 'categoria',
      label: t('common.category'),
      type: 'text' as const,
      placeholder: t('admin.filter_by_category'),
    },
  ], [t]);

  // ===============================
  // CONFIGURACIÓN DE FORMULARIO
  // ===============================

  const formFields = useMemo(() => [
    codigoField,
    descripcionField,
    categoriaField,
    esObligatorioField,
    {
      name: 'orden',
      label: t('common.order'),
      type: 'number' as const,
      placeholder: '1',
      min: 1,
      description: t('admin.order_description'),
      validation: z.coerce.number().min(1).optional(),
    },
    estadoField,
  ], [t]);

  // ===============================
  // MANEJADORES DE EVENTOS
  // ===============================

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFiltros({ ...filtros, ...newFilters });
  };

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, page });
  };

  const handleAdd = () => {
    if (!canCreate) {
      toast.error(t('errors.no_permission'));
      return;
    }
    setFormModal({
      isOpen: true,
      mode: 'create',
    });
  };

  const handleEdit = (item: ItemChequeo) => {
    setFormModal({
      isOpen: true,
      mode: 'edit',
      data: item,
    });
  };

  const handleDelete = async (item: ItemChequeo) => {
    try {
      await eliminarMutation.mutateAsync(item.id);
      toast.success(t('admin.delete_success'));
    } catch (error) {
      toast.error(t('errors.delete_error'));
      console.error('Error eliminando item de chequeo:', error);
    }
  };

  const handleFormSubmit = async (datos: CrearItemChequeo) => {
    try {
      if (formModal.mode === 'create') {
        await crearMutation.mutateAsync(datos);
        toast.success(t('admin.create_success'));
      } else if (formModal.data) {
        await actualizarMutation.mutateAsync({
          id: formModal.data.id,
          datos,
        });
        toast.success(t('admin.update_success'));
      }

      setFormModal({ isOpen: false, mode: 'create' });
    } catch (error: any) {
      const message = error?.response?.data?.detail || t('errors.save_error');
      toast.error(message);
      console.error('Error en formulario item de chequeo:', error);
    }
  };

  const handleFormClose = () => {
    setFormModal({ isOpen: false, mode: 'create' });
  };

  // ===============================
  // ESTADO DE CARGA DEL FORMULARIO
  // ===============================

  const isFormLoading = crearMutation.isLoading || actualizarMutation.isLoading;

  // ===============================
  // RENDER
  // ===============================

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button
          onClick={() => navigate('/admin')}
          className="hover:text-gray-700 transition-colors"
        >
          {t('admin.administration')}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{t('admin.checklist')}</span>
      </div>

      {/* Botón volver */}
      <div className="flex items-center">
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back_to_admin')}</span>
        </button>
      </div>

      {/* Tabla CRUD */}
      <CrudTable
        data={data}
        isLoading={isLoading}
        error={error as Error | null}
        columns={columns}
        filters={filters}
        currentFilters={filtros}
        onFiltersChange={handleFiltersChange}
        onPageChange={handlePageChange}
        onAdd={handleAdd}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        title={t('admin.checklist_title')}
        addButtonText={t('admin.add')}
        canAdd={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {/* Modal de formulario */}
      <CrudForm
        isOpen={formModal.isOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        fields={formFields}
        title={formModal.mode === 'create' ? t('admin.create_checklist_item') : t('admin.edit_item')}
        submitText={formModal.mode === 'create' ? t('common.save') : t('common.save')}
        isLoading={isFormLoading}
        initialData={formModal.data}
        mode={formModal.mode}
      />
    </div>
  );
};

export default ItemsChequeoView;
